/**
 * ForgotPassword Page — Industry design system ("Recuperar Senha" artboard).
 *
 * Single centered 460px blueprint card on the light ground: Z plate +
 * wordmark above, accent-700 eyebrow, uppercase title, one grammar field,
 * full-width primary submit, dashed-top footer ("Voltar ao login" +
 * muted "Link expira em 24h" note).
 *
 * UX behavior preserved from the previous version:
 *   1. Always show a generic success message regardless of whether the
 *      email matched an account (anti-enumeration, mirrors the backend).
 *   2. Submitted state renders the masked email + a 60-second resend
 *      cooldown.
 *   3. Clear "what to do if the email doesn't arrive" guidance.
 *
 * Backend contract — no changes, `authService.requestPasswordReset`
 * posts to `/auth/password-reset/` (24h signed token + email).
 */

import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/auth';
import AccessSupportPanel from '../components/common/AccessSupportPanel';

const RESEND_COOLDOWN_SECONDS = 60;

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';

const labelSx = {
  display: 'block',
  fontFamily: HEADING,
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: MUTED58,
  mb: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: 40,
  fontSize: 14.5,
  background: 'transparent',
  border: '1px solid var(--color-divider)',
  borderRadius: 0,
  padding: '0 11px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
};

const primaryBtnSx = {
  width: '100%',
  minHeight: 44,
  mt: '20px',
  border: `1px solid ${ACC}`,
  background: ACC,
  color: 'var(--color-bg)',
  fontFamily: HEADING,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
  '&:active': { background: ACC7 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const secondaryBtnSx = {
  fontFamily: HEADING,
  fontSize: '11.5px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  minHeight: 38,
  padding: '0 14px',
  cursor: 'pointer',
  border: '1px solid var(--color-divider)',
  background: 'transparent',
  color: 'var(--color-text)',
  borderRadius: 0,
  '&:hover': { borderColor: 'rgba(29,31,32,0.45)' },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

function maskEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  if (local.length <= 2) return `${local[0] || ''}*@${domain}`;
  return `${local.slice(0, 2)}${'*'.repeat(Math.min(local.length - 2, 5))}@${domain}`;
}

function isValidEmail(value) {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.length >= 5 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEmail = (searchParams.get('email') || '').trim();

  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Tick the resend cooldown timer down to zero.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      setError('Informe um e-mail válido para enviarmos o link de recuperação.');
      return;
    }

    if (loading || cooldown > 0) return;

    setLoading(true);
    try {
      await authService.requestPasswordReset(trimmed);
      setSubmitted(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      // Backend returns a generic success response even when the email
      // is unknown, so any error here is transport-level.
      setError(
        err.response?.data?.error
          || err.response?.data?.detail
          || 'Não conseguimos enviar o link de recuperação agora. Tente novamente em instantes.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || loading) return;
    await handleSubmit({ preventDefault: () => {} });
  };

  const handleBackToLogin = () => {
    navigate('/login' + (email ? `?email=${encodeURIComponent(email)}` : ''));
  };

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: '40px 24px',
        boxSizing: 'border-box',
      }}
    >
      {/* Z plate + wordmark */}
      <Box
        component="a"
        href="/login"
        sx={{ display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none', color: 'var(--color-text)' }}
      >
        <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 15, fontWeight: 600 }}>
          Z
        </Box>
        <Box component="span" sx={{ fontFamily: HEADING, fontSize: 19, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Zulbot
        </Box>
      </Box>

      <Box
        className="blueprint"
        sx={{ width: '100%', maxWidth: 460, mt: 3, p: '26px', background: 'transparent', boxShadow: 'var(--ind-shadow-md)' }}
      >
        <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

        <Box sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: ACC7 }}>
          Recuperação de acesso
        </Box>
        <Typography
          component="h1"
          sx={{ m: '8px 0 0', fontFamily: HEADING, fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase' }}
        >
          {submitted ? 'Verifique seu e-mail' : 'Redefinir sua senha'}
        </Typography>
        <Typography sx={{ m: '7px 0 0', fontSize: 13.5, lineHeight: 1.55, color: MUTED72 }}>
          {submitted
            ? 'Se existir uma conta Zulbot associada ao e-mail informado, o link de redefinição já está a caminho.'
            : 'Informe o e-mail cadastrado e enviaremos um link seguro para criar uma nova senha.'}
        </Typography>

        {!submitted ? (
          <form onSubmit={handleSubmit}>
            <Box sx={{ mt: '20px' }}>
              <Box component="label" htmlFor="forgot-email" sx={labelSx}>
                E-mail da conta
              </Box>
              <input
                id="forgot-email"
                className="ind-input"
                name="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                autoFocus
                autoComplete="email"
                inputMode="email"
                placeholder="nome@empresa.com"
                style={inputStyle}
              />
            </Box>

            {error && (
              <Box sx={{ mt: '14px', p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK }}>
                {error}
              </Box>
            )}

            <Box component="button" type="submit" disabled={loading || !email.trim()} sx={primaryBtnSx}>
              {loading ? 'Enviando link...' : 'Enviar link de recuperação'}
            </Box>
          </form>
        ) : (
          <Box>
            <Box
              sx={{
                mt: '20px',
                p: '12px 13px',
                border: `1px solid ${ACC}`,
                background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)',
                fontSize: 13,
                lineHeight: 1.55,
                color: ACC8,
              }}
            >
              Se existir uma conta Zulbot associada a{' '}
              <Box component="strong" sx={{ fontFamily: HEADING, fontWeight: 600 }}>
                {maskEmail(email.trim())}
              </Box>
              {', '}enviamos um link para redefinir sua senha. Verifique também a caixa de spam e promoções.
            </Box>

            <Box sx={{ mt: '18px' }}>
              <Box sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED58, mb: '8px' }}>
                O que fazer agora
              </Box>
              {[
                ['1.', 'Abra o e-mail "Zulbot — Redefinição de senha" (pode levar até 1 minuto).'],
                ['2.', 'Clique no link. Ele é válido por 24 horas e expira após o primeiro uso.'],
                ['3.', 'Defina uma senha forte (mínimo 8 caracteres) e faça login normalmente.'],
              ].map(([n, copy]) => (
                <Box key={n} sx={{ display: 'flex', gap: '9px', alignItems: 'flex-start', py: '5px', borderBottom: '1px dashed var(--color-neutral-200)' }}>
                  <Box component="span" sx={{ flexShrink: 0, display: 'grid', placeItems: 'center', width: 20, height: 18, mt: '1px', fontFamily: HEADING, fontSize: 10, fontWeight: 600, border: '1px solid var(--color-divider)', color: MUTED58 }}>
                    {n}
                  </Box>
                  <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.5 }}>{copy}</Box>
                </Box>
              ))}
            </Box>

            {error && (
              <Box sx={{ mt: '14px', p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK }}>
                {error}
              </Box>
            )}

            <Box
              component="button"
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0 || loading}
              sx={{ ...secondaryBtnSx, width: '100%', mt: '18px' }}
            >
              {cooldown > 0
                ? `Reenviar em ${cooldown}s`
                : loading
                  ? 'Reenviando...'
                  : 'Reenviar link'}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            mt: '18px',
            pt: '15px',
            borderTop: '1px dashed var(--color-neutral-300)',
            flexWrap: 'wrap',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={handleBackToLogin}
            sx={{
              border: 0,
              background: 'transparent',
              p: 0,
              cursor: 'pointer',
              fontFamily: HEADING,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: ACC7,
              '&:hover': { color: ACC8 },
            }}
          >
            Voltar para o login
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'color-mix(in srgb, var(--color-text) 55%, transparent)' }}>
            Link expira em 24h
          </Box>
        </Box>

        <AccessSupportPanel
          sourcePage="forgot-password"
          defaultEmail={email}
          title="O e-mail não chegou?"
          description="Se passaram mais de 10 minutos sem o link, verifique a caixa de spam e abra um ticket — o suporte Zulbot valida manualmente e envia um novo link."
        />
      </Box>
    </Box>
  );
};

export default ForgotPassword;
